import { FunctionComponent, ReactNode } from 'react';
import { Chip } from '../../tailwindcomponents';
import { Person, AccessTime, Groups, NightlightRound, FlightTakeoff, ScheduleOutlined, MenuBook } from '../icons';

type HandoverMetaCardProps = {
  createdBy: string;
  createdDate: string;
  startDate?: string | null;
  endDate?: string | null;
  shift?: string | null;
  isDayShift?: boolean | null;
  /**
   * "To be read by". Pass it - even as an empty string - to show the cell, leave it
   * undefined to omit the cell. An empty value renders a dash rather than disappearing:
   * on the report-style handovers an unassigned reader blocks "Finish report", so it is
   * exactly the cell the user needs to see.
   */
  toBeReadBy?: string | null;
  /**
   * Editable stand-in for the read-only Day/Night chip. When supplied it replaces the
   * chip in the "Type" cell, otherwise the cell is a read-only Chip. A slot rather than
   * an isEditable flag so this card never has to know about Select or the change handler.
   */
  typeControl?: ReactNode;
};

type Field = {
  label: string;
  value: string;
  icon: JSX.Element;
  chip?: { color: 'warning' | 'info'; label: string };
  control?: ReactNode;
};

const iconClass = 'text-muted shrink-0';

const HandoverMetaCard: FunctionComponent<HandoverMetaCardProps> = ({
  createdBy,
  createdDate,
  startDate,
  endDate,
  shift,
  isDayShift,
  toBeReadBy,
  typeControl,
}) => {
  const fields: Field[] = [
    { label: 'Created by', value: createdBy, icon: <Person className={iconClass} /> },
    { label: 'Created date', value: createdDate, icon: <AccessTime className={iconClass} /> },
  ];
  if (startDate) fields.push({ label: 'Start date', value: startDate, icon: <FlightTakeoff className={iconClass} /> });
  if (endDate) fields.push({ label: 'End date', value: endDate, icon: <ScheduleOutlined className={iconClass} /> });
  if (shift) fields.push({ label: 'Shift', value: shift, icon: <Groups className={iconClass} /> });
  // Null checks on purpose, NOT truthiness: `false` means Night and must still render a cell.
  if (isDayShift !== undefined && isDayShift !== null) {
    fields.push({
      label: 'Type',
      value: isDayShift ? 'Day' : 'Night',
      icon: <NightlightRound className={isDayShift ? `${iconClass} opacity-40` : iconClass} />,
      chip: { color: isDayShift ? 'warning' : 'info', label: isDayShift ? 'Day' : 'Night' },
      control: typeControl,
    });
  }
  if (toBeReadBy !== undefined && toBeReadBy !== null) {
    fields.push({ label: 'To be read by', value: toBeReadBy === '' ? '—' : toBeReadBy, icon: <MenuBook className={iconClass} /> });
  }

  return (
    <div className="ds-card p-4 mb-4 relative overflow-hidden">
      <span className="absolute left-0 top-0 bottom-0 w-1 bg-primary" aria-hidden />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pl-3">
        {fields.map(f => (
          <div key={f.label} className="flex flex-col gap-1 min-w-0">
            <span className="text-[11px] uppercase tracking-wider text-muted font-medium">{f.label}</span>
            <div className="flex items-center gap-2 min-w-0">
              {f.icon}
              {f.control
                // `[&_.ds-label]:hidden` suppresses the control's own <label>. Select renders one
                // unconditionally and `.ds-label` is display:block + margin-bottom:4px, so an empty
                // label would add ~20px and knock this cell out of line with its siblings. The cell
                // already supplies the "TYPE" label above.
                ? <div className="min-w-0 flex-1 [&_.ds-label]:hidden">{f.control}</div>
                : f.chip
                  ? <Chip size="small" color={f.chip.color} label={f.chip.label} />
                  : <span className="text-sm font-semibold truncate">{f.value}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default HandoverMetaCard;
